import { useMemo, useState } from "react"
import { ComboboxField } from "@ui/forms"
import { ControllerRenderProps, FieldValues, Path } from "react-hook-form"

import { useFetchHousehold, useFetchHouseholds } from "@/hooks/api/households"

interface HouseholdSelectorFieldProps<T extends FieldValues> {
  field: ControllerRenderProps<T, Path<T>>
  label?: string
  placeholder?: string
  householdType?: "Client" | "Prospect" | "client" | "prospect"
  currentHouseholdId?: string
  bookIdOverride?: string
}

export function HouseholdSelectorField<T extends FieldValues>({
  field,
  label = "Client",
  placeholder = "Select household",
  householdType,
  currentHouseholdId,
  bookIdOverride,
}: HouseholdSelectorFieldProps<T>) {
  const [searchQuery, setSearchQuery] = useState("")
  const { data: households, isLoading } = useFetchHouseholds(searchQuery, householdType, bookIdOverride)
  const selectedHouseholdId = typeof field.value === "string" && field.value.length > 0 ? field.value : undefined
  const { data: selectedHousehold } = useFetchHousehold(selectedHouseholdId ?? "", {
    enabled: !!selectedHouseholdId,
    bookIdOverride,
  })

  const options = useMemo(() => {
    const allHouseholds = households?.households ?? []
    const baseOptions = allHouseholds
      .filter((h) => h.id !== currentHouseholdId)
      .map((h) => ({ value: h.id, label: h.name }))

    if (!selectedHousehold || !selectedHouseholdId || selectedHouseholdId === currentHouseholdId) {
      return baseOptions
    }

    if (baseOptions.some((option) => option.value === selectedHouseholdId)) {
      return baseOptions
    }

    return [{ value: selectedHousehold.id, label: selectedHousehold.name }, ...baseOptions]
  }, [currentHouseholdId, households?.households, selectedHousehold, selectedHouseholdId])

  return (
    <ComboboxField
      id="household_id"
      label={label}
      field={field}
      onSearch={setSearchQuery}
      options={options}
      isSearching={isLoading}
      placeholder={isLoading ? "Loading..." : placeholder}
    />
  )
}
